import React, { useState } from 'react';
import { getEvalTypePillColorClass } from '../../Utils/ColorUtils';
import EvalStatus from '../Utils/EvalStatus';

/**
 * EvalSelectBox Component
 * Reusable component for displaying evaluation instances in a selectable format
 */
const EvalSelectBox = ({ 
  evalItem, 
  isSelected = false, 
  onClick,
  index 
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipText, setTooltipText] = useState('');
  
  // Get pill info for consistent coloring
  const pillInfo = evalItem.evaluations?.type ? getEvalTypePillColorClass(evalItem.evaluations.type) : null;
  
  // Determine border color based on type when selected
  const getSelectedBorderClass = () => {
    if (!isSelected) return 'border-gray-200 shadow-sm hover:shadow-md hover:border-gray-300';
    
    switch (evalItem.evaluations?.type) {
      case 'VSJob':
        return '[border-color:var(--color-brand-purple)] shadow-md';
      case 'VSResume':
        return '[border-color:var(--color-brand-blue)] shadow-md';
      case 'VSSkillset':
        return '[border-color:var(--color-brand-gray)] shadow-md';
      default:
        return 'border-gray-300 shadow-md';
    }
  };
  
  return (
    <div 
      key={index} 
      className={`p-4 cursor-pointer rounded-lg border relative ${getSelectedBorderClass()} bg-white transition-all duration-200`}
      onClick={() => onClick(evalItem)}
    >
      <div className="flex items-center mb-1">
        {/* Icon based on candidate_id presence */}
        <div className="relative mr-2 flex-shrink-0">
          {evalItem.candidate_id ? (
            // Company/Job icon when candidate_id is present
            <div 
              className="cursor-help" 
              onMouseEnter={() => {
                setShowTooltip(true);
                setTooltipText('Candidate Evaluation');
              }}
              onMouseLeave={() => setShowTooltip(false)}
            >
              <svg 
                className="w-4 h-4 text-gray-600" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
          ) : (
            // User/Person icon when candidate_id is not present
            <div 
              className="cursor-help" 
              onMouseEnter={() => {
                setShowTooltip(true);
                setTooltipText('Independent Talent Evaluation');
              }}
              onMouseLeave={() => setShowTooltip(false)}
            >
              <svg 
                className="w-4 h-4 text-gray-600" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
          )}
          
          {/* Custom tooltip */}
          {showTooltip && (
            <div className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-2 py-1 text-xs text-white bg-gray-800 rounded shadow-lg whitespace-nowrap z-50">
              {tooltipText}
              <div className="absolute right-full top-1/2 transform -translate-y-1/2 w-0 h-0 border-t-4 border-b-4 border-r-4 border-transparent border-r-gray-800"></div>
            </div>
          )}
        </div>
        <h3 className="font-medium text-gray-900">
          {evalItem.evaluations?.name || `Evaluation ${evalItem.eval_id || 'N/A'}`}
        </h3>
      </div>
      <p className="text-xs text-gray-500 mb-2">
        Assigned: {new Date(evalItem.created_at).toLocaleDateString()} {new Date(evalItem.created_at).toLocaleTimeString()} 
      </p>
      <div className="flex items-center justify-between mt-1">
        {/* Status - far left */}
        <EvalStatus status={evalItem.status} showDot={true} size="sm" />
        
        {/* Type pill and AI icon - far right */}
        <div className="flex items-center space-x-2">
          {/* Small AI loading icon when status is not evs_available */}
          {evalItem.evaluations.status !== 'evs_available' && (
            <div className="flex items-center">
              <div className="relative w-5 h-5">
                {/* Central node */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-yellow-600 rounded-full animate-pulse"></div>
                
                {/* Surrounding nodes with staggered animations */}
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-yellow-500 rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></div>
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-yellow-500 rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
                <div className="absolute top-1/2 left-0 transform -translate-y-1/2 w-1 h-1 bg-yellow-500 rounded-full animate-pulse" style={{animationDelay: '0.6s'}}></div>
                <div className="absolute top-1/2 right-0 transform -translate-y-1/2 w-1 h-1 bg-yellow-500 rounded-full animate-pulse" style={{animationDelay: '0.8s'}}></div>
                
                {/* Corner nodes for more neural network feel */}
                <div className="absolute top-1 left-1 w-0.5 h-0.5 bg-yellow-400 rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
                <div className="absolute top-1 right-1 w-0.5 h-0.5 bg-yellow-400 rounded-full animate-pulse" style={{animationDelay: '1.2s'}}></div>
                <div className="absolute bottom-1 left-1 w-0.5 h-0.5 bg-yellow-400 rounded-full animate-pulse" style={{animationDelay: '1.4s'}}></div>
                <div className="absolute bottom-1 right-1 w-0.5 h-0.5 bg-yellow-400 rounded-full animate-pulse" style={{animationDelay: '1.6s'}}></div>
              </div>
            </div>
          )}
          
          {/* Type pill */}
          {evalItem.evaluations?.type && (() => {
            const pillInfo = getEvalTypePillColorClass(evalItem.evaluations.type);
            return (
              <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-md ${pillInfo.colorClasses}`}>
                {pillInfo.text}
              </span>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default EvalSelectBox;
